/**
 * AdminTracksTab Component
 * Course/Track management tab for admin panel - with create/edit functionality
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Settings, CheckCircle, AlertCircle, Plus, Edit, Trash2, BookOpen, Loader2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

const COURSE_ICONS = ['📚', '🎓', '💼', '📊', '🏆', '⚡', '🔧', '📈', '🎯', '💡', '🚀', '⭐'];
const COURSE_COLORS = [
  { value: '#0047AB', label: 'Blue' },
  { value: '#059669', label: 'Green' },
  { value: '#7c3aed', label: 'Purple' },
  { value: '#dc2626', label: 'Red' },
  { value: '#ea580c', label: 'Orange' },
  { value: '#0891b2', label: 'Cyan' },
  { value: '#4f46e5', label: 'Indigo' },
  { value: '#be185d', label: 'Pink' }
];

// CourseForm must be defined OUTSIDE the main component to prevent focus loss
const CourseForm = ({ course, onFieldChange, isNew = false }) => {
  const handleCodeChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '');
    onFieldChange('code', value);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Course Code *</Label>
          <Input
            value={course.code}
            onChange={handleCodeChange}
            placeholder="e.g. ITIL, PRINCE2"
            disabled={!isNew}
            maxLength={20}
            data-testid="course-code-input"
          />
          <p className="text-xs text-slate-500">Uppercase letters, numbers, underscores only</p>
        </div>
        <div className="space-y-2">
          <Label>Course Name *</Label>
          <Input
            value={course.name}
            onChange={(e) => onFieldChange('name', e.target.value)}
            placeholder="e.g. ITIL 4 Foundation"
            data-testid="course-name-input"
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea
          value={course.description}
          onChange={(e) => onFieldChange('description', e.target.value)}
          placeholder="Brief description of the certification..."
          rows={2}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Icon</Label>
          <div className="flex flex-wrap gap-2">
            {COURSE_ICONS.map(icon => (
              <button
                key={icon}
                type="button"
                onClick={() => onFieldChange('icon', icon)}
                className={`w-10 h-10 rounded-lg text-xl flex items-center justify-center transition-all ${
                  course.icon === icon ? 'bg-blue-100 ring-2 ring-blue-500' : 'bg-slate-100 hover:bg-slate-200'
                }`}
              >
                {icon}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <Label>Color</Label>
          <div className="flex flex-wrap gap-2">
            {COURSE_COLORS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => onFieldChange('color', value)}
                className={`w-10 h-10 rounded-lg transition-all ${
                  course.color === value ? 'ring-2 ring-offset-2 ring-slate-400' : ''
                }`}
                style={{ backgroundColor: value }}
                title={label}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label>Price (€)</Label>
          <Input
            type="number"
            value={(course.price_cents / 100).toFixed(2)}
            onChange={(e) => onFieldChange('price_cents', Math.round(parseFloat(e.target.value || 0) * 100))}
            min="0"
            step="0.01"
          />
        </div>
        <div className="space-y-2">
          <Label>Validity (months)</Label>
          <Select 
            value={String(course.validity_months)} 
            onValueChange={(v) => onFieldChange('validity_months', parseInt(v))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">3 months</SelectItem>
              <SelectItem value="6">6 months</SelectItem>
              <SelectItem value="12">12 months</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Passing Score (%)</Label>
          <Input
            type="number"
            value={course.passing_score}
            onChange={(e) => onFieldChange('passing_score', parseInt(e.target.value || 0))}
            min="50"
            max="100"
          />
        </div>
        <div className="space-y-2">
          <Label>Exam Duration (min)</Label>
          <Input
            type="number"
            value={course.exam_duration_minutes}
            onChange={(e) => onFieldChange('exam_duration_minutes', parseInt(e.target.value || 0))}
            min="10"
            max="240"
          />
        </div>
      </div>

      <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
        <div>
          <Label className="text-base">Course Active</Label>
          <p className="text-sm text-slate-500">Enable to show in store and allow purchases</p>
        </div>
        <Switch
          checked={course.is_active}
          onCheckedChange={(checked) => onFieldChange('is_active', checked)}
        />
      </div>
    </div>
  );
};

const AdminTracksTab = ({
  courses,
  coursesLoading,
  handleCreateCourse,
  handleUpdateCourse,
  handleDeleteCourse,
  handleTrackToggle,
  fetchCourses
}) => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [newCourse, setNewCourse] = useState({
    code: '',
    name: '',
    description: '',
    icon: '📚',
    color: '#0047AB',
    price_cents: 2900,
    validity_months: 6,
    passing_score: 70,
    exam_duration_minutes: 60,
    is_active: true
  });

  const resetNewCourse = () => {
    setNewCourse({
      code: '',
      name: '',
      description: '',
      icon: '📚',
      color: '#0047AB',
      price_cents: 2900,
      validity_months: 6,
      passing_score: 70,
      exam_duration_minutes: 60,
      is_active: true
    });
  };

  // Stable field change handler for new course
  const handleNewCourseFieldChange = (field, value) => {
    setNewCourse(prev => ({ ...prev, [field]: value }));
  };

  // Stable field change handler for editing course
  const handleEditCourseFieldChange = (field, value) => {
    setEditingCourse(prev => prev ? { ...prev, [field]: value } : prev);
  };

  const handleCreate = async () => {
    setSaving(true);
    const success = await handleCreateCourse(newCourse);
    setSaving(false);
    if (success) {
      setIsCreateDialogOpen(false);
      resetNewCourse();
    }
  };

  const handleEdit = async () => {
    setSaving(true);
    const success = await handleUpdateCourse(editingCourse.code, editingCourse);
    setSaving(false);
    if (success) {
      setIsEditDialogOpen(false);
      setEditingCourse(null);
    }
  };

  if (coursesLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#0047AB]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-white dark:bg-slate-900">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Course Management
            </CardTitle>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#0047AB] hover:bg-[#003380] text-white" data-testid="create-course-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Course
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Course</DialogTitle>
                </DialogHeader>
                <CourseForm 
                  course={newCourse} 
                  onFieldChange={handleNewCourseFieldChange} 
                  isNew={true} 
                />
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
                  <Button 
                    onClick={handleCreate} 
                    disabled={!newCourse.code || !newCourse.name || saving}
                    className="bg-[#0047AB] hover:bg-[#003380]"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Create Course
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
            Manage your certification courses. Default courses (PMP, CAPM, PSM, PSPO) cannot be deleted but can be customized.
            Create new courses to expand your training offerings.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(courses || []).map((course) => (
              <Card 
                key={course.code} 
                className={`border-2 ${course.is_active !== false ? 'border-green-500' : 'border-slate-300 dark:border-slate-700'}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl"
                        style={{ backgroundColor: `${course.color || '#0047AB'}20` }}
                      >
                        {course.icon || '📚'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-lg">{course.code}</h4>
                          {course.is_default && (
                            <Badge variant="outline" className="text-xs">Default</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{course.name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {course.is_active !== false ? (
                        <Badge className="bg-green-500 text-white">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-amber-500 text-amber-600">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Inactive
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {course.description && (
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">{course.description}</p>
                  )}
                  
                  <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                    <span className="flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      {course.question_count || 0} questions
                    </span>
                    <span>€{((course.price_cents || 2900) / 100).toFixed(2)}</span>
                    <span>{course.validity_months || 6} months</span>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingCourse({
                          ...course,
                          price_cents: course.price_cents || 2900,
                          validity_months: course.validity_months || 6,
                          passing_score: course.passing_score || 70,
                          exam_duration_minutes: course.exam_duration_minutes || 60,
                          is_active: course.is_active !== false
                        });
                        setIsEditDialogOpen(true);
                      }}
                      data-testid={`edit-course-${course.code}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    
                    <Button
                      variant={course.is_active !== false ? "outline" : "default"}
                      size="sm"
                      onClick={() => handleTrackToggle(course.code, course.is_active === false)}
                      className={course.is_active === false ? 'bg-green-600 hover:bg-green-700' : ''}
                    >
                      {course.is_active !== false ? 'Disable' : 'Enable'}
                    </Button>
                    
                    {!course.is_default && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-course-${course.code}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Course</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete <strong>{course.name}</strong>?
                              <br /><br />
                              {course.question_count > 0 ? (
                                <span className="text-red-600">
                                  This course has {course.question_count} questions. You must delete all questions first.
                                </span>
                              ) : (
                                <span>This action cannot be undone.</span>
                              )}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteCourse(course.code)}
                              className="bg-red-600 hover:bg-red-700"
                              disabled={course.question_count > 0}
                            >
                              Delete Course
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Course: {editingCourse?.code}</DialogTitle>
          </DialogHeader>
          {editingCourse && (
            <CourseForm 
              course={editingCourse} 
              onFieldChange={handleEditCourseFieldChange} 
              isNew={false} 
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleEdit} 
              disabled={saving}
              className="bg-[#0047AB] hover:bg-[#003380]"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTracksTab;
